import router from '@/router/index'

import {
  getPreferredGmf,
  getActiveGmfSubs,
  getGmfConfig,
  getTableColumnKeys,
} from '@/store/gameformat/gameformat'
import { numRoundTo5bb, getUniqueKeys, SNK, toggleArr, getUniqueArr } from '@/helpers/helpers'
import { isEqual, omit } from 'lodash'

const getInitialState = ({ namespace }) => ({
  gmfs_namespace: namespace,
  gmfs_gameformat: null,

  // for multiple options in stacksTable
  gmfs_depthList: [],
  gmfs_stacksList: [],
  gmfs_avgStackList: [],

  // for GmfSelektorStacksTable - get/set handled straight via component
  gmfs_table_selectedFilters: {
    stacks_type: [],
    opening_type: [],
    tournament_phase: [],
    players: [],
    variant: [],
    depth: [],
    cash_drop: [],
    biggest_stack: [],
    type: [],
    rake: [],
    opening_size: [],
    _3bet_size: [],
    simplified_position: [],
    favorite: false,
    streams: false,
  },

  gmfs_table_sortKey: '0',
  gmfs_table_sortOrder: 'desc',

  // gmfs_gmfTable_game: null,
  gmfs_gmfTable_before: null,
  gmfs_gmfTable_trainer: null,
  gmfs_multiselect: false,

  gmfs_activeSubsForTable: null,
  gmfs_tableConf: null,
  gmfs_tableRows_base: [],
  gmfs_tableFilters_base: [],

  // these events have been solved using regular ante instead of big blind ante
  // eventually once solutions are fixed this should be removed
  gmfs_inaccurate_events: [
    'WSOP-2022-EVENT70-10000',
    'WSOP-2023-EVENT76-10000',
    'WSOP-2024-MAIN-EVENT81-10000',
    'WSOP-2023-EVENT40-250K-SUPER-HIGH-ROLLER',
  ],

  gmfs_table_activeICMFilters:
    'gmfs_table_activeICMFilters' in localStorage
      ? JSON.parse(localStorage.getItem('gmfs_table_activeICMFilters'))
      : [],

  gmfs_table_inputsOpen:
    'gmfs_table_inputsOpen' in localStorage
      ? JSON.parse(localStorage.getItem('gmfs_table_inputsOpen'))
      : false,
})

const getters = {
  gmfs_state: state =>
    omit(state, [
      'gmfs_namespace',
      'gmfs_gmfTable_before',
      'gmfs_gmfTable_trainer',
      'gmfs_activeSubsForTable',
      'gmfs_tableConf',
      'gmfs_tableRows_base',
      'gmfs_tableFilters_base',
    ]),
  gmfs_tableMultiselect: (state, getters, rootState, rootGetters) => () => {
    // functional getters recomputes every time
    const res =
      ['PracticeTrainer', 'PracticeDrills', 'PracticeHands'].includes(
        router.currentRoute.value.name,
      ) && getters.fh_start_spot !== 'custom_spot'
    return res
  },
  gmfs_gridView:
    (state, getters, rootState, rootGetters) =>
    ({ isCash3456789HuMaxSTRpreflop, gameformat }) => {
      // functional getters recomputes every time
      const gridViewPossible =
        getters.fh_start_spot !== 'custom_spot' &&
        ['PracticeTrainer', 'PracticeDrills'].includes(router.currentRoute.value.name)
          ? true
          : isCash3456789HuMaxSTRpreflop
            ? false
            : gameformat?.format === 'icm'
              ? false
              : gameformat?.format === 'icm_events'
                ? false
                : gameformat?.format === 'chipev'
                  ? gameformat?.players === 8 && gameformat?.solution_completeness === 'all_spots'
                  : true

      const res = rootGetters.gmfStacksTableAsGrid && gridViewPossible
      // console.log('gmfs_gridView', gameformat, isCash3456789HuMaxSTRpreflop, res)
      return res
    },

  gmfs_table_selectedFilters: state => state.gmfs_table_selectedFilters,
  gmfs_gameformat: state => state.gmfs_gameformat,
  gmfs_depthList: state => state.gmfs_depthList,
  gmfs_stacksList: state => state.gmfs_stacksList,
  gmfs_avgStackList: state => state.gmfs_avgStackList,
  gmfs_table_sortKey: state => state.gmfs_table_sortKey,
  gmfs_table_sortOrder: state => state.gmfs_table_sortOrder,
  gmfs_table_activeICMFilters: state => state.gmfs_table_activeICMFilters,
  gmfs_table_inputsOpen: state => state.gmfs_table_inputsOpen,
  gmfs_tableConf: state => state.gmfs_tableConf,
  gmfs_beforeTableRows: (state, getters, rootState, rootGetters) => {
    const { all, hasMultiselect } = state.gmfs_activeSubsForTable
    const mm = getters.gmfs_tableMultiselect()

    const game = [
      ...(all.group ? [all.group] : []),

      // ...(all.rake ? [all.rake] : []),
      // cash
      ...(all.cash_type ? [all.cash_type] : []),
      ...(all.shortstack_type ? [all.shortstack_type] : []),

      ...(all.format ? [all.format] : []),
      ...(all.solution_completeness ? [all.solution_completeness] : []),
      // ...(!mm && all.solution_completeness ? [all.solution_completeness] : []),
      ...(all.format_straddle ? [all.format_straddle] : []),
      // spins and husng
      ...(all.husng_or_spins_type ? [all.husng_or_spins_type] : []),
      // mtt
      ...(all.format_mtt ? [all.format_mtt] : []),

      ...(all.tournament_group ? [all.tournament_group] : []),
      ...(all.tournament_name ? [all.tournament_name] : []),
      ...(all.tournament_type ? [all.tournament_type] : []),
      ...(all.tournament_players ? [all.tournament_players] : []),
      ...(all.tournament_phase ? [all.tournament_phase] : []),

      // rest
      ...(all.players ? [all.players] : []),
      ...(all.variant?.id === '4max' ? [all.variant] : []),

      // ...(mm && all.solution_completeness ? [all.solution_completeness] : []),
      ...(all.spots_preflop_postflop ? [all.spots_preflop_postflop] : []),

      ...(all.type ? [all.type] : []),

      ...(all.rake ? [all.rake] : []),
    ]

    const rightTop = hasMultiselect
      ? [
          ...(all.opening_type ? [all.opening_type] : []),

          ...(all.type_opening_size ? [all.type_opening_size] : []),
          // ...(all.rake ? [all.rake] : []),
          ...(all._3bet_size ? [all._3bet_size] : []),

          ...(all.stacks ? [all.stacks] : []),
          ...(all.variant ? [all.variant] : []),
        ]
      : [
          // ...(all.rake ? [all.rake] : []),
          ...(all.type_opening_size ? [all.type_opening_size] : []),
          ...(all._3bet_size ? [all._3bet_size] : []),
        ]

    const res = {
      game,
      rightTop,
    }
    // console.log('gmfs_beforeTableRows', all, res)
    return res
  },
  gmfs_rightBottom: (state, getters, rootState, rootGetters) => {
    if (getters.gmfs_tableMultiselect()) return null

    const gmf = state.gmfs_gameformat
    const nestedGmfs = rootState.gameformat.gameformats_nested_study
    const all = getActiveGmfSubs({
      acc: [],
      item: nestedGmfs,
      activeFormat: gmf,
      block_ids: ['opening_type', 'variant', 'stacks', 'type'],
    })
    const res = [
      ...(all.opening_type ? [all.opening_type] : []),
      ...(all.variant && all.variant.id !== '4max' ? [all.variant] : []),
      ...(all.stacks ? [all.stacks] : []),
      ...(all.type ? [all.type] : []),
    ]
    return res
  },
  gmfs_tableFilters: (state, getters, rootState, rootGetters) => {
    const selectedFilters = state.gmfs_table_selectedFilters
    let shrinkingPool = state.gmfs_tableConf.conf.gameformats
    let res = state.gmfs_tableFilters_base.map(i => {
      const available = getUniqueKeys({ arr: shrinkingPool, key: i.valId })
      const disabled = i.options.filter(oo => !available.includes(oo))

      let selected = selectedFilters[i.id]
      if (selected.length) {
        selected = getters.gmfs_tableMultiselect() ? selected : [selected[0]]
        shrinkingPool = shrinkingPool.filter(gmf => selected.includes(gmf[i.valId]))
      }
      const r = {
        ...i,
        selected,
        disabled,
      }
      return r
    })
    // res = res.filter(i => i.options.length !== i.disabled.length)
    res = res.filter(i => i.options.length !== i.disabled.length && i.options.length > i.hideLength)

    // console.log('gmfs_tableFilters', res)
    return res
  },
  gmfs_tableRows_filtered: (state, getters, rootState, rootGetters) => {
    let res = state.gmfs_tableRows_base
    const { isCash3456789HuMaxSTRpreflop } = state.gmfs_tableConf
    const tableFilters = getters.gmfs_tableFilters
    const selectedFilters = state.gmfs_table_selectedFilters
    const mm = getters.gmfs_tableMultiselect()

    const activeFilters = tableFilters.filter(ff => ff.selected.length)
    if (activeFilters.length) {
      if (isCash3456789HuMaxSTRpreflop && !mm) {
        res = res.filter(gmf => activeFilters.every(ff => ff.selected.includes(gmf[ff.valId])))
      } else {
        res = res.map(row => {
          const filteredGameformats = (row.notfiltered_gameformats || []).filter(gmf =>
            activeFilters.every(ff => ff.selected.some(fv => fv === gmf[ff.valId])),
          )
          row.gameformats = filteredGameformats
          return row
        })
        res = res.filter(row => row.gameformats.length)
      }
    }

    const gmfFavoriteIds = rootGetters.gameformats_favorite_ids
    res = res.map((row, idx) => {
      row.is_favorite = row.notfiltered_gameformats
        ? row.notfiltered_gameformats.every(gmf => gmfFavoriteIds.includes(gmf.id))
        : gmfFavoriteIds.includes(row.id)
      return row
    })

    const gridView = getters.gmfs_gridView({
      isCash3456789HuMaxSTRpreflop,
      gameformat: state.gmfs_gmfTable_before,
    })

    if (!gridView && !mm && selectedFilters.favorite && res.some(i => i.is_favorite)) {
      res = res.filter(row => row.is_favorite)
    }

    if (
      !gridView &&
      !mm &&
      selectedFilters.streams &&
      res.some(i => i.gameformats?.[0]?.stream_url)
    ) {
      res = res.filter(i => i.gameformats?.[0]?.stream_url)
    }

    // console.log('gmfs_tableRows_filtered', res)
    return res
  },
  gmfs_tableRows: (state, getters, rootState, rootGetters) => {
    let rows = getters.gmfs_tableRows_filtered

    const {
      isCash3456789HuMaxSTRpreflop,
      isCashShortstack,
      depthRow,
      isAnte4max,
      stacksRow,
      avgStackRow,
      isPreflopOnly,
      isCash6max,
      isCashHu,
      isCash9max,
    } = state.gmfs_tableConf
    const hasMultiselect = getters.gmfs_tableMultiselect()

    const depthList = state.gmfs_depthList.length ? state.gmfs_depthList : null
    const stacksList = state.gmfs_stacksList.length ? state.gmfs_stacksList : null
    const avgStackList = state.gmfs_avgStackList.length ? state.gmfs_avgStackList : null

    if (hasMultiselect) {
      rows = rows.map(row => {
        let selected = false

        const avgStack = row.avgStack || row.gameformats?.[0]?.avg_stack
        if (avgStackRow && avgStackList) {
          selected = avgStackList.includes(avgStack)
        } else if (depthRow && depthList) {
          selected = depthList.includes(row.row_id)
        } else if (stacksRow && stacksList) {
          selected = stacksList.includes(row.row_id)
        } else if (isCash3456789HuMaxSTRpreflop && depthList) {
          selected = depthList.includes(row.depth)
        }

        const r = {
          ...row,
          avgStack,
          selected,
          highlighted: hasMultiselect ? selected : false,
          depthRow,
        }
        return r
      })
    } else {
      const { gmfs_gameformat } = state
      rows = rows.map(row => {
        const avgStack = row.avgStack || row.gameformats?.[0]?.avg_stack
        const active = isCash3456789HuMaxSTRpreflop
          ? gmfs_gameformat.id === row.id
          : Object.keys(row.conditions).every(key => gmfs_gameformat[key] === row.conditions[key])

        const r = {
          ...row,
          avgStack,
          active,
          selected: false,
          highlighted: active,
          depthRow,
        }
        return r
      })
    }

    const gridView = getters.gmfs_gridView({
      isCash3456789HuMaxSTRpreflop,
      gameformat: state.gmfs_gmfTable_before,
    })
    const sortOrder = gridView ? 'desc' : getters.gmfs_table_sortOrder
    const sortKey = gridView ? 0 : Number(getters.gmfs_table_sortKey)

    if (isCash3456789HuMaxSTRpreflop && !stacksRow && !isCashShortstack) {
      const sortKeys = getTableColumnKeys({
        isPreflopOnly,
        isCashHu,
        isCash6max,
        isAnte4max,
        isCash9max,
      })

      const key = sortKeys[sortKey]
      const order = getGmfConfig()[key]?.order

      if (key === 'stack_rounding' || key === 'simplified_position' || key === 'variant') {
        if (sortOrder === 'asc') {
          rows.sort((a, b) => (!b.cells[sortKey] || a.cells[sortKey] < b.cells[sortKey] ? -1 : 1))
        } else {
          rows.sort((a, b) => (!b.cells[sortKey] || a.cells[sortKey] > b.cells[sortKey] ? -1 : 1))
        }
      } else if (order) {
        if (sortOrder === 'asc') {
          rows.sort((a, b) => (!b[key] || order.indexOf(a[key]) < order.indexOf(b[key]) ? -1 : 1))
        } else {
          rows.sort((a, b) => (!b[key] || order.indexOf(a[key]) > order.indexOf(b[key]) ? -1 : 1))
        }
      }
      // all else
    } else if (sortOrder === 'asc') {
      rows.sort((a, b) => (!b.cells[sortKey] || a.cells[sortKey] < b.cells[sortKey] ? -1 : 1))
    } else {
      rows.sort((a, b) => (!b.cells[sortKey] || a.cells[sortKey] > b.cells[sortKey] ? -1 : 1))
    }

    return rows
  },

  gmfs_gmfWithMostPlayers: (state, getters, rootState, rootGetters) => {
    const allAny =
      !state.gmfs_depthList?.length &&
      !state.gmfs_stacksList?.length &&
      !state.gmfs_avgStackList?.length

    const selectedRows = allAny
      ? getters.gmfs_tableRows
      : getters.gmfs_tableRows.filter(i => i.selected)

    let gmfs = selectedRows.reduce(
      (a, b) => [
        ...a,
        ...(b.gameformats ? b.gameformats : [b]), // cash table rows are just gameformats
      ],
      [],
    )
    gmfs = gmfs.sort((a, b) => b.players - a.players)
    const res = gmfs[0]
    return res
  },
  gmfs_inaccurate_events: state => state.gmfs_inaccurate_events,
}

const actions = {
  gmfs_set_gameformat(
    { state, dispatch, commit, getters, rootGetters },
    {
      gameformat = null,
      force_recomputeBase = false,
      instaApply = false,
      routerParams,
      keepHistoryTree,
      hasLists,
      channelId,
    } = {},
  ) {
    let res = gameformat || JSON.parse(JSON.stringify(rootGetters.gameformat))

    if (!res) {
      return
    }

    if (res.legacy && !rootGetters.showLegacySolutions) {
      // dirty hack - but cannot think of anything better
      // note - if someone curently has legacy and opens the selector
      // he will see different format than he has - this one time
      // so here query_gameformat and gmfs_gameformat will differ
      res = rootGetters.gameformats.find(i => i.group === res.group && !i.legacy)
    }

    dispatch('gmfs_set', { key: 'gmfs_gameformat', value: res })
    dispatch('gmfs_checkForRecomputeBase', { gameformat: res, force_recomputeBase })

    if (instaApply) {
      dispatch('gmfs_apply', { routerParams, keepHistoryTree, hasLists, channelId })
    }
  },
  gmfs_checkForRecomputeBase(
    { state, dispatch, commit, getters, rootGetters },
    { gameformat, force_recomputeBase } = {},
  ) {
    const gmf = gameformat || state.gmfs_gameformat

    const gmfs_gmfTable_before = {
      group: gmf.group,
      format: gmf.format,
      format_straddle: gmf.format_straddle,
      shortstack_type: gmf.shortstack_type,

      cash_type: gmf.cash_type,
      rake: gmf.rake,

      format_mtt: gmf.format_mtt,
      tournament_group: gmf.tournament_group,
      tournament_name: gmf.tournament_name,
      tournament_type: gmf.tournament_type,
      tournament_players: gmf.tournament_players,
      tournament_phase: gmf.tournament_phase,
      players: gmf.players,
      variant: gmf.variant,

      husng_or_spins_type: gmf.husng_or_spins_type,
      solution_completeness: gmf.solution_completeness,
      spots_preflop_postflop: gmf.spots_preflop_postflop,
    }
    const gmfs_gmfTable_trainer = {
      type: gmf.type,

      opening_type: gmf.opening_type,
      variant: gmf.variant,

      rake: gmf.rake,
      type_opening_size: gmf.type_opening_size,
      _3bet_size: gmf._3bet_size,
    }

    const gmfs_multiselect = getters.gmfs_tableMultiselect()
    let recomputeBase = false
    if (JSON.stringify(gmfs_gmfTable_before) !== JSON.stringify(state.gmfs_gmfTable_before)) {
      dispatch('gmfs_set', { key: 'gmfs_gmfTable_before', value: gmfs_gmfTable_before })
      recomputeBase = true
    }
    if (JSON.stringify(gmfs_gmfTable_trainer) !== JSON.stringify(state.gmfs_gmfTable_trainer)) {
      dispatch('gmfs_set', { key: 'gmfs_gmfTable_trainer', value: gmfs_gmfTable_trainer })
      recomputeBase = recomputeBase || getters.gmfs_tableMultiselect()
    }
    if (gmfs_multiselect !== state.gmfs_multiselect) {
      dispatch('gmfs_set', { key: 'gmfs_multiselect', value: gmfs_multiselect })
      recomputeBase = true
    }

    // debugger
    if (recomputeBase || force_recomputeBase) {
      dispatch('gmfs_recomputeBase')
    }
  },
  gmfs_recomputeBase({ state, dispatch, commit, getters, rootGetters }) {
    dispatch('gmfs_set_activeSubsForTable')
    dispatch('gmfs_set_tableConf')
    dispatch('gmfs_set_tableRows_base')
    dispatch('gmfs_set_tableFilters_base')
  },
  gmfs_set_activeSubsForTable({ state, rootState, dispatch, commit, getters, rootGetters }) {
    // this gmfs_gmfTable start is primary hook
    const hasMultiselect = getters.gmfs_tableMultiselect()
    const gmfParams = hasMultiselect
      ? {
          ...state.gmfs_gmfTable_before,
          ...state.gmfs_gmfTable_trainer,
        }
      : state.gmfs_gmfTable_before

    let all = {}
    const key = hasMultiselect ? 'trainer' : 'study'
    const nestedGmfs = rootState.gameformat[`gameformats_nested_${key}`]
    all = getActiveGmfSubs({
      acc: all,
      item: nestedGmfs,
      activeFormat: gmfParams,
    })
    dispatch('gmfs_set', { key: 'gmfs_activeSubsForTable', value: { all, hasMultiselect } })
  },
  gmfs_set_tableConf({ state, dispatch, commit, getters, rootGetters }) {
    const { all, hasMultiselect } = state.gmfs_activeSubsForTable
    const { gmfs_gameformat } = state

    const {
      type_cash,
      shortstack_type,
      depth_mtt_hu,
      depth_mtt_chipev_8,
      stacks_mtt_chipev_8,
      solution_completeness,
      depth_cash,
      depth_husng,
      stacks_spins,
      stacks_mtt,
      stacks_cash,
      avg_stack,
      depth_mtt_chiv_ev_preflop_only,
      tournament_name,
    } = all

    // const tournament_players = all.tournament_players

    const isPreflopOnly = gmfs_gameformat.preflop_only
    const isCash3456789HuMaxSTRpreflop =
      gmfs_gameformat.group === 'cash' &&
      ((gmfs_gameformat.cash_type === 'ante_straddle' &&
        gmfs_gameformat.solution_completeness === 'preflop_only') ||
        gmfs_gameformat.format === '3max' ||
        gmfs_gameformat.format === '4max' ||
        gmfs_gameformat.format === '5max' ||
        gmfs_gameformat.format === '6max' ||
        gmfs_gameformat.format === '7max' ||
        gmfs_gameformat.format === '8max' ||
        gmfs_gameformat.format === '9max' ||
        (gmfs_gameformat.format === 'hu' && isPreflopOnly))

    const isCashHu = isCash3456789HuMaxSTRpreflop && gmfs_gameformat.format === 'hu'
    const isCash9max = isCash3456789HuMaxSTRpreflop && gmfs_gameformat.format === '9max'
    const isCash6max = isCash3456789HuMaxSTRpreflop && gmfs_gameformat.format === '6max'
    const isCashShortstack = gmfs_gameformat.group === 'cash' && !!shortstack_type
    const isAnte4max = gmfs_gameformat.group === 'cash' && gmfs_gameformat.format === '4max'

    const showStackRounding =
      gmfs_gameformat.rake === 'GGrc' &&
      gmfs_gameformat.group === 'cash' &&
      gmfs_gameformat.format === '6max' &&
      gmfs_gameformat.solution_completeness === 'preflop_only' &&
      gmfs_gameformat.type === 'general'

    const avgStackRow = hasMultiselect && (!!stacks_mtt || !!avg_stack)

    const conf =
      type_cash ||
      depth_mtt_hu ||
      depth_mtt_chipev_8 ||
      depth_mtt_chiv_ev_preflop_only ||
      stacks_mtt_chipev_8 ||
      depth_cash ||
      depth_husng ||
      stacks_cash ||
      stacks_mtt ||
      stacks_spins ||
      avg_stack ||
      tournament_name

    const isMttICM = gmfs_gameformat.format === 'icm'
    const isMttChipEv = gmfs_gameformat.format === 'chipev'
    const isMttChipEv8max = isMttChipEv && !!depth_mtt_chipev_8

    const stacksRow = !avgStackRow && (!!stacks_mtt || !!stacks_spins)
    const depthRow = !!(
      depth_husng ||
      depth_cash ||
      type_cash ||
      depth_mtt_hu ||
      depth_mtt_chipev_8 ||
      depth_mtt_chiv_ev_preflop_only
    )
    const hasAvgStackCell = !!stacks_mtt || isCashShortstack

    const isStraddle =
      gmfs_gameformat.cash_type === 'straddle' || gmfs_gameformat.cash_type === 'ante_straddle'

    const isICMEvents = gmfs_gameformat.format === 'icm_events'

    const res = {
      conf,
      hasLock: conf?.hasLock,
      isSpins: !!stacks_spins,
      hasMultiselect,
      hasSTR: isStraddle && gmfs_gameformat.solution_completeness === 'all_spots',
      hasSTRpreflop: isStraddle && gmfs_gameformat.solution_completeness === 'preflop_only',
      hasAvgStackCell,

      avgStackRow, // average stack
      stacksRow,
      depthRow,

      hasStacksTypes: !!stacks_mtt && !solution_completeness && !hasMultiselect,
      hasVariant: !!stacks_mtt && (depth_mtt_chipev_8 || solution_completeness),
      isAnte4max,

      isCashHu,
      isCash9max,
      isCash6max,
      isCashShortstack,
      isCash3456789HuMaxSTRpreflop,
      showStackRounding,
      isPreflopOnly,
      isMttICM,
      isMttChipEv,
      isMttChipEv8max,
      isICMEvents,
    }

    // console.log('gmfs_set_tableConf', res)
    dispatch('gmfs_set', { key: 'gmfs_tableConf', value: res })
  },
  gmfs_set_tableRows_base({ state, dispatch, commit, getters, rootGetters }) {
    const {
      isCash3456789HuMaxSTRpreflop,
      isCashShortstack,
      depthRow,
      stacksRow,
      avgStackRow,
      hasAvgStackCell,
      isPreflopOnly,
      isCashHu,
      isCash9max,
      isCash6max,
      showStackRounding,
      isAnte4max,
      isICMEvents,
      conf,
    } = state.gmfs_tableConf

    const mm = getters.gmfs_tableMultiselect()
    let res = !mm && isCash3456789HuMaxSTRpreflop ? conf.gameformats : conf.subs

    const rowIdKey = stacksRow ? 'stacks' : avgStackRow ? 'avgStack' : 'id'

    const showStackRoundingTrainer = showStackRounding && mm
    if (showStackRoundingTrainer) {
      res = res.map(row => {
        const stack_rounding = row.gameformats[0].stack_rounding
        let drop = row.gameformats[0].cash_drop
        drop = drop === 'no_cash_drop' ? '' : `& ${drop}`

        const display_stack_rounding = drop ? `${stack_rounding} ${drop}` : stack_rounding
        return {
          ...row,
          stack_rounding,
          display_stack_rounding,
        }
      })
      res = getUniqueArr({ arr: res, key: 'display_stack_rounding' })
    }

    if (isICMEvents) {
      // If it's ICM Event we need to add tournament_hand_number as the first column
      res = res.map(row => {
        const tournament_hand_number = row.gameformats[0].tournament_hand_number
        return {
          ...row,
          tournament_hand_number,
        }
      })
    }

    if (avgStackRow) {
      res = res.map(row => {
        const avgStack = row.gameformats[0].avg_stack
        return {
          ...row,
          avgStack,
        }
      })

      res = getUniqueArr({ arr: res, key: 'avgStack' })
    }

    // TODO rerefactor gmf001 - or fix sorting
    const columns = getTableColumnKeys({
      isPreflopOnly,
      isCashHu,
      isCash6max,
      isAnte4max,
      isCash9max,
    })
    const getCashColumns = gmf => columns.map(col => gmf[col])

    // debugger
    res = res.map(row => {
      const cc = {
        id: row.id,
        avgStack: row.avgStack || row.gameformats?.[0]?.avg_stack,
        depth: row.depth || row.gameformats?.[0]?.depth,
        stack_rounding: row.stack_rounding,
        display_stack_rounding: row.display_stack_rounding,
        tournament_hand_number: row.tournament_hand_number,
      }

      const stacks = stacksRow
        ? (row.stacks || row.id).split('-').map(i => numRoundTo5bb(Number(i)))
        : depthRow
          ? [numRoundTo5bb(row.stack_rounding || row.depth || row.id)]
          : '??'

      let cells = null
      let displayCells = null
      let cellStyles = []

      if (showStackRoundingTrainer) {
        cells = [cc.stack_rounding]
        displayCells = [cc.display_stack_rounding]
      } else if (isCash3456789HuMaxSTRpreflop && !stacksRow && !mm && !isCashShortstack) {
        const gmf = row
        cells = getCashColumns(gmf)
      } else if (!mm && isCashShortstack) {
        cells = [cc.avgStack, ...row.stacks.split('-').map(i => numRoundTo5bb(Number(i)))]
      } else if (avgStackRow) {
        cells = [cc.avgStack]
      } else if (hasAvgStackCell && isICMEvents) {
        cells = [cc.tournament_hand_number, cc.avgStack, ...stacks]
      } else if (hasAvgStackCell) {
        cells = [cc.avgStack, ...stacks]
      } else {
        cells = stacks
      }

      if (stacksRow) {
        const min = Math.min(...stacks)
        const max = Math.max(...stacks)
        if (min !== max) {
          // map up and down arrows to max and min stack sizes
          cellStyles = stacks.map(ss =>
            ss === min
              ? '--clr-dot-hover:var(--clr-player_stack-short);--rotate:0deg'
              : ss === max && row.gameformats?.[0].group !== 'spins'
                ? '--clr-dot-hover:var(--clr-player_stack-big);--rotate:180deg'
                : '',
          )
          // handles correct placement of "Big", "Short" arrows (yellow, blue) - ignore non-stack columns
          const prefix_cells_count = cells.length - stacks.length
          cellStyles =
            prefix_cells_count > 0
              ? [...Array(prefix_cells_count).fill(''), ...cellStyles]
              : cellStyles
        }
      }

      const r = {
        ...row,
        gameformats: row.gameformats,
        notfiltered_gameformats: row.gameformats,
        id: row.id,
        row_id: cc[rowIdKey] || row.id, // stacksRow
        avgStack: cc.avgStack,
        depth: cc.depth,
        cells,
        displayCells: displayCells || cells,
        cellStyles,
      }
      return r
    })
    // console.log('gmfs_tableRows_base', res)
    dispatch('gmfs_set', { key: 'gmfs_tableRows_base', value: res })
  },
  gmfs_set_tableFilters_base({ state, dispatch, commit, getters, rootGetters }) {
    const {
      isCash3456789HuMaxSTRpreflop,
      isPreflopOnly,
      isSpins,
      isCashShortstack,
      hasStacksTypes,
      isMttICM,
      isMttChipEv8max,
      isAnte4max,
      isCashHu,
      isCash6max,
      isICMEvents,
      conf,
    } = state.gmfs_tableConf
    const mm = getters.gmfs_tableMultiselect()
    const gmfConf = getGmfConfig()

    let res = [
      {
        on: hasStacksTypes,
        hasAny: !mm,
        // isSingle: true,
        isSingle: !mm,
        id: 'stacks_type',
        title_tooltip:
          conf.conditions.group === 'mtt'
            ? 'tooltips.gmf_stacks_type_mtt'
            : conf.conditions.group === 'spins'
              ? 'tooltips.gmf_stacks_type_spins'
              : 'tooltips.gmf_stacks',
      },
      {
        on: isSpins && mm,
        hasAny: true,
        isSingle: false,
        id: 'opening_type',
        title_tooltip: 'tooltips.gmf_opening_type',
      },
      {
        on: isSpins,
        hasAny: true,
        isSingle: !mm,
        id: 'stacks_type',
        title_tooltip:
          conf.conditions.group === 'mtt'
            ? 'tooltips.gmf_stacks_type_mtt'
            : conf.conditions.group === 'spins'
              ? 'tooltips.gmf_stacks_type_spins'
              : 'tooltips.gmf_stacks',
      },
      {
        on: isMttICM,
        hasAny: true,
        isSingle: !mm,
        id: 'type',
        title_tooltip: 'tooltips.gmf_type_rest',
        getTooltipHtml: gmfConf.type.getTooltipHtml,
      },
      {
        on: isSpins,
        hasAny: true,
        isSingle: !mm,
        id: 'biggest_stack',
        title: 'gameformat.spins_biggest_stack',
        title_tooltip: 'tooltips.gmf_spins_biggest_stacks',
        getCssClass: options => (options.length > 9 ? 'cherow_grid' : ''),
        order: 'desc',
      },
      {
        on: isMttICM && mm,
        hasAny: true,
        isSingle: false,
        id: 'tournament_phase',
        title_tooltip: 'tooltips.gmf_tournament_phase',
      },
      {
        on: (isMttICM || isICMEvents) && mm,
        hasAny: true,
        isSingle: false,
        id: 'players',
        title_tooltip: 'tooltips.gmf_players',
      },
      {
        on: isMttChipEv8max && mm,
        hasAny: true,
        isSingle: false,
        id: 'variant',
        valId: 'variant',
        title: 'gameformat.variant',
        title_tooltip: 'tooltips.gmf_variant',
        order: gmfConf.variant.order,
      },
      {
        on: isCash3456789HuMaxSTRpreflop && !mm && !isCashShortstack,
        hasAny: true,
        isSingle: !mm,
        id: 'depth',
        valId: 'stack_rounding',
        title: 'gameformat.stacks',
        title_tooltip: 'tooltips.gmf_stacks',
        order: 'desc',
        css_class: 'gmfstckstbl_checkbox-row_width-auto',
      },
      {
        on: isCash3456789HuMaxSTRpreflop && !mm,
        hasAny: true,
        isSingle: !mm,
        id: 'type',
        title: 'gameformat.bet_sizes',
        help_dialog: 'study-betsizes-help-dialog',
        css_class: 'gmfstckstbl_checkbox-row_width-auto',
      },
      {
        on: isCash3456789HuMaxSTRpreflop && !mm,
        hasAny: true,
        isSingle: !mm,
        id: 'rake',
        getTooltip: gmfConf.rake.getTooltip,
        css_class: 'gmfstckstbl_checkbox-row_width-auto',
        order: gmfConf.rake.order,
      },
      // hasSTRpreflop
      // {
      //   on: mm && hasSTRpreflop,
      //   hasAny: true,
      //   isSingle: !mm,
      //   id: 'rake',
      //   getTooltip: gmfConf.rake.getTooltip,
      //   css_class: 'gmfstckstbl_checkbox-row_width-auto',
      //   order: gmfConf.rake.order,
      // },
      {
        on: isCash3456789HuMaxSTRpreflop && (isCashHu ? isPreflopOnly : true),
        hasAny: true,
        isSingle: !mm,
        id: 'opening_size',
        title_tooltip: 'tooltips.gmf_cash_opening_size',
        css_class: 'gmfstckstbl_checkbox-row_width-auto',
        order: gmfConf.opening_size.order,
      },
      {
        on: isCash6max && isPreflopOnly,
        hasAny: true,
        isSingle: !mm,
        id: 'cash_drop',
        hideLength: 1,
        title: 'gameformat.cash_drop',
        title_tooltip: 'tooltips.gmf_cash_drop',
        order: 'desc',
        css_class: 'gmfstckstbl_checkbox-row_width-auto',
      },
      {
        on: isCash3456789HuMaxSTRpreflop && !isCashHu && !isPreflopOnly,
        hasAny: true,
        isSingle: !mm,
        id: '_3bet_size',
        title_tooltip: 'tooltips.gmf_cash__3bet_size',
        css_class: 'gmfstckstbl_checkbox-row_width-auto',
      },
      {
        on: isCash3456789HuMaxSTRpreflop && !isCashHu && !mm,
        hasAny: true,
        isSingle: !mm,
        id: 'simplified_position',
        title: 'gameformat.simplified_position',
        title_tooltip: 'tooltips.gmf_simplified_position',
        css_class: 'gmfstckstbl_checkbox-row_width-auto',
        order: 'desc',
      },
      {
        // on: isCashHu,
        on: isCashHu || isAnte4max,
        hasAny: true,
        isSingle: !mm,
        id: 'variant',
        valId: 'variant',
        title: 'gameformat.variant',
        title_tooltip: 'tooltips.gmf_variant_limps',
        css_class: 'gmfstckstbl_checkbox-row_width-auto',
        order: gmfConf.variant.order,
      },
    ].filter(i => i.on)

    if (res.length === 0) {
      dispatch('gmfs_set', { key: 'gmfs_tableFilters_base', value: [] })
      return
    }

    const pool = conf.gameformats
    res = res
      .map(i => {
        const valId = i.valId || i.id
        const options = getUniqueKeys({ arr: pool, key: valId }).filter(o => o)

        if (options.length <= 1) {
          return null
        }

        const subsConf = gmfConf[i.id]
        const { order } = subsConf
        if (order) {
          if (order === 'asc') {
            options.sort((a, b) => (a < b ? -1 : 1))
          } else if (order === 'desc') {
            options.sort((a, b) => (a > b ? -1 : 1))
          } else {
            options.sort((a, b) => (order.indexOf(a) < order.indexOf(b) ? -1 : 1))
          }
        }
        const locks = options.map(o => {
          const optionGmfs = pool.filter(g => g[valId] === o)
          // console.log('optionGmfs', optionGmfs, o)
          const hasLock = optionGmfs.every(g => !g.subscription_tier)
          return hasLock
        })

        const isSingle = options.length <= 1 ? true : !!i.isSingle
        const hasAny = !!i.hasAny

        const r = {
          id: i.id,
          valId,
          title: i.title || `gameformat.${i.id}`,
          title_tooltip: i.getTooltipHtml
            ? i.getTooltipHtml(options)
            : i.getTooltip
              ? i.getTooltip(conf)
              : i.title_tooltip || '',
          help_dialog: i.help_dialog || '',
          css_class: i.getCssClass ? i.getCssClass(options) : i.css_class,
          asGrid: i.asGrid,
          getOptionName: subsConf.getOptionName,
          hasAny,
          isSingle,
          hideLength: i.hideLength || 0,
          options,
          locks,
          selected: [],
          disabled: [],
        }
        return r
      })
      .filter(i => i)

    // console.log('gmfs_tableFilters_base', res)
    dispatch('gmfs_set', { key: 'gmfs_tableFilters_base', value: res })
  },

  gmfs_set_tableRow(
    { state, rootGetters, dispatch, commit, getters },
    { item, instaApply = false, routerParams, keepHistoryTree, hasLists, channelId },
  ) {
    const { isCash3456789HuMaxSTRpreflop } = state.gmfs_tableConf
    const mm = getters.gmfs_tableMultiselect()
    if (isCash3456789HuMaxSTRpreflop && !mm) {
      dispatch('gmfs_set_gameformat', {
        gameformat: item,
        instaApply,
        routerParams,
        keepHistoryTree,
        hasLists,
        channelId,
      })
      return
    }
    const { conf } = state.gmfs_tableConf
    const res = {
      ...item,
      selected: [item.id],
      conf,
    }
    dispatch('gmfs_update_sub', {
      item: res,
      instaApply,
      routerParams,
      keepHistoryTree,
      hasLists,
      channelId,
    })
  },
  gmfs_update_sub(
    { state, dispatch, commit, getters, rootGetters },
    { item, instaApply = false, routerParams, keepHistoryTree, hasLists, channelId },
  ) {
    const { gmfs_gameformat } = getters

    const id = item.selected[0]
    const selectedSub = item.conf.subs.find(i => i.id === id)

    if (!selectedSub) {
      return
    }

    const rowId = item.id
    // console.log(gmfs_gameformat)
    let compareKeys =
      rowId === 'group'
        ? [
            'spins', //
            'husng',
          ].includes(gmfs_gameformat?.group) &&
          [
            'spins', //
            'husng',
          ].includes(selectedSub.gameformats[0]?.group)
          ? [
              `husng_or_spins_type`, //
              `type`,
            ]
          : []
        : ['icm_events'].includes(selectedSub.gameformats[0]?.format)
          ? [
              'tournament_group', //
              'tournament_name',
              'tournament_phase',
              'players',
            ]
          : ['icm'].includes(selectedSub.gameformats[0]?.format)
            ? [
                'tournament_type', //
                'tournament_phase',
                'tournament_players',
                'players',
              ]
            : ['chipev'].includes(selectedSub.gameformats[0]?.format)
              ? [
                  'variant', //
                ]
              : []

    const compareKeyIdx = compareKeys.findIndex(kk => kk === rowId)
    compareKeys = compareKeys.filter((kk, idx) => idx > compareKeyIdx)
    compareKeys = compareKeys.filter(kk => selectedSub.gameformats[0]?.[kk])

    const gameformats = selectedSub.gameformats || []
    const res = getPreferredGmf({
      gameformats,
      gameformat: gmfs_gameformat,
      compareKeys,
    })

    if (!res) {
      console.log(item)
      debugger
    }

    dispatch('gmfs_set_gameformat', {
      gameformat: res,
      instaApply,
      routerParams,
      keepHistoryTree,
      hasLists,
      channelId,
    })
  },
  gmfs_toggle_list(
    { state, dispatch, commit, getters },
    {
      listKey = 'depthList',
      list = null,
      value,
      empty = false,
      fill = false,
      instaApply = false,
      routerParams,
      keepHistoryTree,
      hasLists,
      channelId,
    },
  ) {
    let res = []
    if (list) {
      res = list
    } else if (fill) {
      res = getters.gmfs_tableRows.map(i => i.row_id)
    } else if (!empty) {
      res = toggleArr({ arr: state[listKey], item: value })
    }
    state[listKey] = res
    if (instaApply) {
      dispatch('gmfs_apply', { routerParams, keepHistoryTree, hasLists, channelId })
    }
  },
  gmfs_update_detached({ state, dispatch, commit, getters, rootGetters }, { channelId }) {
    if (!channelId) return
    dispatch(
      'bc_postMessage',
      {
        channelId,
        data: {
          code: 'any_gmfs_update',
          value: {
            namespace: state.gmfs_namespace,
            gmfs_state: JSON.parse(JSON.stringify(getters.gmfs_state)),
          },
        },
      },
      { root: true },
    )
  },
  gmfs_apply(
    { state, dispatch, commit, getters, rootGetters },
    {
      routerParams = {
        push: false,
        replace: false,
        saveLocally: false,
      },
      keepHistoryTree = false,
      hasLists = false,
      channelId = '',
    } = {},
  ) {
    const {
      gmfs_gameformat,
      gmfs_depthList,
      gmfs_avgStackList,
      gmfs_stacksList,
      gmfs_table_sortKey,
      gmfs_table_sortOrder,
      gmfs_table_selectedFilters: gmff,
    } = state
    if (channelId) {
      dispatch('gmfs_update_detached', { channelId })
    }

    const newOne = JSON.stringify(gmfs_gameformat)
    const currOne = JSON.stringify(getters.gameformat)
    const currSolType = getters.query_solution_type

    const query = {
      solution_type: 'gwiz',
      stacks: gmfs_gameformat.stacks,
      gametype: gmfs_gameformat.gametype,
      depth: gmfs_gameformat.depth,

      gmff_stacks_type: gmff.stacks_type,

      gmff_opening_type: gmff.opening_type,
      gmff_tournament_phase: gmff.tournament_phase,
      gmff_players: gmff.players,
      gmff_variant: gmff.variant,

      gmff_biggest_stack: gmff.biggest_stack,

      gmff_depth: gmff.depth,
      gmff_cash_drop: gmff.cash_drop,
      gmff_type: gmff.type,
      gmff_rake: gmff.rake,
      gmff_opening_size: gmff.opening_size,
      gmff__3bet_size: gmff._3bet_size,
      gmff_simplified_position: gmff.simplified_position,
      gmff_favorite: gmff.favorite,

      gmfft_sort_key: gmfs_table_sortKey.toString(),
      gmfft_sort_order: gmfs_table_sortOrder.toString(),

      depth_list: hasLists ? gmfs_depthList : '',
      stacks_list: hasLists ? gmfs_stacksList : '',
      average_stack: hasLists ? gmfs_avgStackList : '',
    }

    const { query_gmfSelector } = getters
    if (isEqual(query_gmfSelector, query)) return
    dispatch('router_update_query', {
      query,
      ...routerParams,
    })

    if (currSolType === 'custom') {
      dispatch(SNK('gamestate_reset_history', state.gmfs_namespace), {
        namespace: state.gmfs_namespace,
      })
      dispatch(
        'api_poker_history',
        {
          namespace: state.gmfs_namespace,
          enable_hsWatcher: true,
        },
        { root: true },
      )
      return
    }

    if (!(keepHistoryTree && currOne !== newOne)) return

    const keepHistory = rootGetters.mergedUserSettings.gmfKeepHistory
    if (keepHistory) {
      const prevGmf = JSON.parse(currOne)
      dispatch(
        'api_poker_history',
        {
          namespace: state.gmfs_namespace,
          prevGmf,
          enable_hsWatcher: true,
        },
        { root: true },
      )
    } else {
      dispatch(
        SNK('gamestate_reset_history', state.gmfs_namespace),
        {
          namespace: state.gmfs_namespace,
        },
        { root: true },
      )
      dispatch(
        'api_poker_history',
        {
          namespace: state.gmfs_namespace,
          enable_hsWatcher: true,
        },
        { root: true },
      )
    }
  },

  gmfs_reset({ state, commit }, initialState) {
    commit('gmfs_reset', initialState)
  },
  gmfs_set({ state, commit }, { key, value }) {
    commit('gmfs_set', { key, value })
    if (key === 'gmfs_table_activeICMFilters') {
      localStorage.setItem('gmfs_table_activeICMFilters', JSON.stringify(value))
    }
    if (key === 'gmfs_table_inputsOpen') {
      localStorage.setItem('gmfs_table_inputsOpen', JSON.stringify(value))
    }
  },
}

const mutations = {
  gmfs_reset(state, initialState) {
    Object.assign(state, initialState || getInitialState({ namespace: state.gmfs_namespace }))
  },
  gmfs_set(state, { key, value }) {
    state[key] = value
  },
}

function getGmfSelektorModule(namespace) {
  return {
    state: getInitialState({ namespace }),
    getters,
    actions,
    mutations,
  }
}
export { getGmfSelektorModule }
